import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import rafSchedule from 'raf-schd';
import { getInlineNodeViewProducer } from '@atlaskit/editor-common/react-node-view';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { DATASOURCE_INNER_CONTAINER_CLASSNAME } from '@atlaskit/editor-common/styles';
import { canRenderDatasource } from '@atlaskit/editor-common/utils';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import { eventsFromTransaction } from '../analytics/events-from-tr';
import { BlockCard } from '../nodeviews/blockCard';
import { Datasource } from '../nodeviews/datasource';
import { EmbedCard } from '../nodeviews/embedCard';
import { InlineCardNodeView } from '../nodeviews/inlineCard';
import { setCardLayoutAndDatasourceTableRef, setDatasourceTableRef } from './actions';
import { pluginKey } from './plugin-key';
import reducer from './reducers';
import { handleProvider, resolveWithProvider } from './util/resolve';
import { getNewRequests, getPluginState, getPluginStateWithUpdatedPos } from './util/state';
export { pluginKey } from './plugin-key';
export var createPlugin = function createPlugin(options, pluginInjectionApi) {
  return function (pmPluginFactoryParams) {
    var editorAppearance = options.editorAppearance,
      platform = options.platform,
      allowResizing = options.allowResizing,
      useAlternativePreloader = options.useAlternativePreloader,
      fullWidthMode = options.fullWidthMode,
      showServerActions = options.showServerActions,
      cardPluginEvents = options.cardPluginEvents;
    var inlineCardViewProducer = getInlineNodeViewProducer({
      pmPluginFactoryParams: pmPluginFactoryParams,
      Component: InlineCardNodeView,
      extraComponentProps: {
        useAlternativePreloader: useAlternativePreloader,
        showServerActions: showServerActions
      }
    });
    return new SafePlugin({
      state: {
        init: function init() {
          return {
            requests: [],
            provider: null,
            cards: [],
            showLinkingToolbar: false,
            smartLinkEvents: undefined,
            editorAppearance: editorAppearance,
            showDatasourceModal: false,
            datasourceModalType: undefined,
            datasourceTableRef: undefined,
            layout: undefined
          };
        },
        apply: function apply(tr, pluginState, prevEditorState) {
          // Update all the positions of outstanding requests and
          // cards in the plugin state.
          var pluginStateWithUpdatedPos = getPluginStateWithUpdatedPos(pluginState, tr);

          // apply any actions
          var meta = tr.getMeta(pluginKey);
          if (cardPluginEvents) {
            var events = eventsFromTransaction(tr, prevEditorState);
            cardPluginEvents.push.apply(cardPluginEvents, _toConsumableArray(events));
          }
          if (meta) {
            return reducer(pluginStateWithUpdatedPos, meta);
          }
          return pluginStateWithUpdatedPos;
        }
      },
      view: function view(_view) {
        var subscriptionHandler = function subscriptionHandler(name, provider) {
          return handleProvider(name, provider, _view);
        };
        var domAtPos = _view.domAtPos.bind(_view);
        var rafCancellationCallbacks = [];
        pmPluginFactoryParams.providerFactory.subscribe('cardProvider', subscriptionHandler);
        return {
          update: function update(view, prevState) {
            var _selection$node;
            var currentState = getPluginState(view.state);
            var oldState = getPluginState(prevState);
            var state = view.state,
              dispatch = view.dispatch;
            var selection = state.selection,
              tr = state.tr,
              schema = state.schema;
            var isBlockCardSelected = selection instanceof NodeSelection && ((_selection$node = selection.node) === null || _selection$node === void 0 ? void 0 : _selection$node.type) === schema.nodes.blockCard;
            if (isBlockCardSelected) {
              var _findDomRefAtPos, _node$attrs;
              var datasourceTableRef = (_findDomRefAtPos = findDomRefAtPos(selection.from, domAtPos)) === null || _findDomRefAtPos === void 0 ? void 0 : _findDomRefAtPos.querySelector(".".concat(DATASOURCE_INNER_CONTAINER_CLASSNAME));
              var node = selection.node;
              var isDatasource = !!(node !== null && node !== void 0 && (_node$attrs = node.attrs) !== null && _node$attrs !== void 0 && _node$attrs.datasource);
              var shouldUpdateTableRef = datasourceTableRef && (currentState === null || currentState === void 0 ? void 0 : currentState.datasourceTableRef) !== datasourceTableRef;
              if (isDatasource && shouldUpdateTableRef) {
                var _node$attrs2;
                // since we use the plugin state, which is a shared state, we need to update the datasourceTableRef, layout on each selection
                var layout = (node === null || node === void 0 ? void 0 : (_node$attrs2 = node.attrs) === null || _node$attrs2 === void 0 ? void 0 : _node$attrs2.layout) || 'center';

                // we use cardAction to set the same meta, hence, we will need to combine both layout+datasourceTableRef in one transaction
                dispatch(setCardLayoutAndDatasourceTableRef({
                  datasourceTableRef: datasourceTableRef,
                  layout: layout
                })(tr));
              }
            } else {
              if (currentState !== null && currentState !== void 0 && currentState.datasourceTableRef) {
                dispatch(setDatasourceTableRef(undefined)(tr));
              }
            }
            if (currentState && currentState.provider) {
              // Find requests in this state that weren't in the old one.
              var newRequests = getNewRequests(oldState, currentState);
              // Ask the CardProvider to resolve all new requests.
              var provider = currentState.provider;
              newRequests.forEach(function (request) {
                /**
                 * Queue each asynchronous resolve request on separate frames.
                 * ---
                 * NB: The promise for each request is queued to take place on separate animation frames. This avoids
                 * the scenario debugged and discovered in EDM-668, wherein the queuing of too many promises in quick succession
                 * leads to the browser's macrotask queue being overwhelmed, locking interactivity of the browser tab.
                 * By using this approach, the browser is free to schedule the resolution of the promises below in between rendering/network/
                 * other tasks as per common implementations of the JavaScript event loop in browsers.
                 */
                var invoke = rafSchedule(function () {
                  var _pluginInjectionApi$a, _pluginInjectionApi$a2, _pluginInjectionApi$a3, _pluginInjectionApi$a4;
                  return resolveWithProvider(view, provider, request, options, pluginInjectionApi === null || pluginInjectionApi === void 0 ? void 0 : (_pluginInjectionApi$a = pluginInjectionApi.analytics) === null || _pluginInjectionApi$a === void 0 ? void 0 : _pluginInjectionApi$a.actions, (_pluginInjectionApi$a2 = pluginInjectionApi === null || pluginInjectionApi === void 0 ? void 0 : (_pluginInjectionApi$a3 = pluginInjectionApi.analytics) === null || _pluginInjectionApi$a3 === void 0 ? void 0 : (_pluginInjectionApi$a4 = _pluginInjectionApi$a3.sharedState.currentState()) === null || _pluginInjectionApi$a4 === void 0 ? void 0 : _pluginInjectionApi$a4.createAnalyticsEvent) !== null && _pluginInjectionApi$a2 !== void 0 ? _pluginInjectionApi$a2 : undefined);
                });
                rafCancellationCallbacks.push(invoke.cancel);
                invoke();
              });
            }

            /**
             * If there have been any events queued, flush them
             * so subscribers can now be notified and dispatch
             * analytics events
             */
            cardPluginEvents === null || cardPluginEvents === void 0 ? void 0 : cardPluginEvents.flush();
          },
          destroy: function destroy() {
            // Cancel any outstanding raf callbacks.
            rafCancellationCallbacks.forEach(function (cancellationCallback) {
              return cancellationCallback();
            });
            pmPluginFactoryParams.providerFactory.unsubscribe('cardProvider', subscriptionHandler);
          }
        };
      },
      props: {
        nodeViews: {
          inlineCard: inlineCardViewProducer,
          blockCard: function blockCard(node, view, getPos, decorations) {
            var _node$attrs3;
            var portalProviderAPI = pmPluginFactoryParams.portalProviderAPI,
              eventDispatcher = pmPluginFactoryParams.eventDispatcher;
            var reactComponentProps = {
              platform: platform,
              showServerActions: showServerActions
            };
            var hasIntlContext = true;
            var isDatasource = !!(node !== null && node !== void 0 && (_node$attrs3 = node.attrs) !== null && _node$attrs3 !== void 0 && _node$attrs3.datasource);
            if (isDatasource) {
              var _node$attrs4, _node$attrs4$datasour;
              if (options.allowDatasource && platform !== 'mobile' && canRenderDatasource(node === null || node === void 0 ? void 0 : (_node$attrs4 = node.attrs) === null || _node$attrs4 === void 0 ? void 0 : (_node$attrs4$datasour = _node$attrs4.datasource) === null || _node$attrs4$datasour === void 0 ? void 0 : _node$attrs4$datasour.id)) {
                return new Datasource({
                  node: node,
                  view: view,
                  getPos: getPos,
                  portalProviderAPI: portalProviderAPI,
                  eventDispatcher: eventDispatcher,
                  hasIntlContext: hasIntlContext,
                  pluginInjectionApi: pluginInjectionApi
                }).init();
              } else {
                return inlineCardViewProducer(node, view, getPos, decorations);
              }
            }
            return new BlockCard(node, view, getPos, portalProviderAPI, eventDispatcher, reactComponentProps, undefined, true, undefined, hasIntlContext).init();
          },
          embedCard: function embedCard(node, view, getPos) {
            var portalProviderAPI = pmPluginFactoryParams.portalProviderAPI,
              eventDispatcher = pmPluginFactoryParams.eventDispatcher,
              dispatchAnalyticsEvent = pmPluginFactoryParams.dispatchAnalyticsEvent;
            var reactComponentProps = {
              eventDispatcher: eventDispatcher,
              allowResizing: allowResizing,
              platform: platform,
              fullWidthMode: fullWidthMode,
              dispatchAnalyticsEvent: dispatchAnalyticsEvent,
              pluginInjectionApi: pluginInjectionApi
            };
            var hasIntlContext = true;
            return new EmbedCard(node, view, getPos, portalProviderAPI, eventDispatcher, reactComponentProps, undefined, true, undefined, hasIntlContext).init();
          }
        }
      },
      key: pluginKey
    });
  };
};